import { v4 as uuid } from 'uuid';
import { type StateCreator } from 'zustand';
import { AudioLevelMonitor, type AudioStatus } from './AudioLevelMonitor';
import { type AudioGraphStore } from './types';

export interface AudioLevelMonitorSlice {
  audioLevelMonitor: AudioLevelMonitor | null;
  audioStatus: AudioStatus;
  audioLevelMonitorUsers: Set<string>;
  initializeAudioLevelMonitor: () => Promise<string>;
  releaseAudioLevelMonitor: (id: string) => void;
}

export const createAudioLevelMonitorSlice: StateCreator<
  AudioGraphStore,
  [],
  [],
  AudioLevelMonitorSlice
> = (set, get) => ({
  audioLevelMonitor: null,
  audioStatus: 'UNKNOWN',
  audioLevelMonitorUsers: new Set(),

  initializeAudioLevelMonitor: async () => {
    const id = uuid();
    const state = get();
    const newUsers = new Set(state.audioLevelMonitorUsers).add(id);

    if (newUsers.size === 1) {
      const { analyzer: micAnalyzer } = await get().getMicAnalyzerTask();
      const { analyzer: tabAnalyzer } = await get().getTabAnalyzerTask();

      const audioLevelMonitor = new AudioLevelMonitor(micAnalyzer, tabAnalyzer, {
        peakWindow: 1000,
        onStatusChange: (status: AudioStatus) => set({ audioStatus: status }),
      });

      audioLevelMonitor.start();
      set({ audioLevelMonitor, audioLevelMonitorUsers: newUsers });
    } else {
      set({ audioLevelMonitorUsers: newUsers });
    }

    return id;
  },

  releaseAudioLevelMonitor: (id: string) => {
    set((state) => {
      const newUsers = new Set(state.audioLevelMonitorUsers);
      newUsers.delete(id);

      if (newUsers.size === 0 && state.audioLevelMonitor) {
        state.audioLevelMonitor.stop();

        return {
          audioLevelMonitorUsers: newUsers,
          audioLevelMonitor: null,
          audioStatus: 'UNKNOWN',
        };
      }

      return { audioLevelMonitorUsers: newUsers };
    });
  },
});
